import React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { LuInstagram, LuYoutube, LuFacebook, LuTwitter } from 'react-icons/lu'
import { SiTiktok } from 'react-icons/si'
import { generate as shortUuid } from 'short-uuid'

import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'

export interface DeliverableItem {
  id: string
  platform: string
  format: string
  notes: string | null
  order: number
  dueDate?: string | null
  sameAsDeliverableId?: string | null
}

const PLATFORM_OPTIONS = ['Instagram', 'TikTok', 'YouTube']

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  Instagram: <LuInstagram className="h-3.5 w-3.5" />,
  TikTok: <SiTiktok className="h-3.5 w-3.5" />,
  YouTube: <LuYoutube className="h-3.5 w-3.5" />,
  Facebook: <LuFacebook className="h-3.5 w-3.5" />,
  X: <LuTwitter className="h-3.5 w-3.5" />,
}

const PLATFORM_FORMATS: Record<string, string[]> = {
  Instagram: ['Reel', 'Post', 'Story'],
  TikTok: ['Video'],
  YouTube: ['Short', 'Video'],
  Facebook: ['Reel', 'Post', 'Story', 'Video'],
  X: ['Post', 'Video'],
}

const getFormatsForPlatform = (platform: string) => PLATFORM_FORMATS[platform] ?? ['Post', 'Video']

export const createDefaultDeliverable = (): DeliverableItem => ({
  id: shortUuid(),
  platform: 'Instagram',
  format: 'Reel',
  notes: null,
  order: 0,
  dueDate: null,
  sameAsDeliverableId: null,
})

interface DeliverableBuilderProps {
  value: DeliverableItem[]
  onChange: (items: DeliverableItem[]) => void
  hideDueDate?: boolean
  hideNotes?: boolean
  hideSameAs?: boolean
  notesLabel?: string
  notesPlaceholder?: string
}

export default function DeliverableBuilder({
  value,
  onChange,
  hideDueDate = false,
  hideNotes = false,
  hideSameAs = false,
  notesLabel = 'Notes',
  notesPlaceholder = 'Optional specs like duration, talking points, or posting notes',
}: DeliverableBuilderProps) {
  const items = value.length ? value : [createDefaultDeliverable()]

  const updateItem = (index: number, updates: Partial<DeliverableItem>) => {
    onChange(
      items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...updates } : item)),
    )
  }

  const removeItem = (index: number) => {
    const removedId = items[index].id
    const next = items
      .filter((_, itemIndex) => itemIndex !== index)
      // Clear any same-as references pointing to the removed item
      .map((item) =>
        item.sameAsDeliverableId === removedId ? { ...item, sameAsDeliverableId: null } : item,
      )
    onChange(next.length ? next : [createDefaultDeliverable()])
  }

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const otherItems = items.filter((_, i) => i !== index)
        const sameAsTarget = item.sameAsDeliverableId
          ? items.find((i) => i.id === item.sameAsDeliverableId)
          : null

        return (
          <div
            key={item.id}
            className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3"
          >
            {/* Header row */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Deliverable {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeItem(index)}
                className="h-7 px-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Platform + Format */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                value={item.platform}
                onValueChange={(platform: string) => {
                  const formats = getFormatsForPlatform(platform)
                  const format = formats.includes(item.format) ? item.format : formats[0]
                  updateItem(index, { platform, format })
                }}
              >
                <SelectTrigger className="border-white/10 bg-white/5 text-white">
                  <SelectValue placeholder="Platform">
                    <span className="flex items-center gap-2">
                      {PLATFORM_ICONS[item.platform]}
                      {item.platform}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-[#1c2a35] border-white/15 text-white">
                  {PLATFORM_OPTIONS.map((platform) => (
                    <SelectItem
                      key={platform}
                      value={platform}
                      className="focus:bg-white/10 focus:text-white text-gray-200"
                    >
                      <span className="flex items-center gap-2">
                        {PLATFORM_ICONS[platform]}
                        {platform}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={item.format}
                onValueChange={(format: string) => updateItem(index, { format })}
              >
                <SelectTrigger className="border-white/10 bg-white/5 text-white">
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent className="bg-[#1c2a35] border-white/15 text-white">
                  {getFormatsForPlatform(item.platform).map((format) => (
                    <SelectItem
                      key={format}
                      value={format}
                      className="focus:bg-white/10 focus:text-white text-gray-200"
                    >
                      {format}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            {!hideNotes && (
              <div className="space-y-1.5">
                <Label className="text-gray-200">{notesLabel}</Label>
                <Textarea
                  value={item.notes || ''}
                  onChange={(e) => updateItem(index, { notes: e.target.value || null })}
                  placeholder={notesPlaceholder}
                  className="min-h-[72px] border-white/10 bg-white/5 text-white placeholder:text-gray-500"
                />
              </div>
            )}

            {/* Due Date + Same As */}
            {(!hideDueDate || (!hideSameAs && otherItems.length > 0)) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {!hideDueDate && (
                  <div className="space-y-1.5">
                    <Label className="text-gray-200">
                      Due date{' '}
                      <span className="text-gray-500 font-normal normal-case tracking-normal">
                        (planning only)
                      </span>
                    </Label>
                    <Input
                      type="date"
                      value={item.dueDate || ''}
                      onChange={(e) => updateItem(index, { dueDate: e.target.value || null })}
                      className="border-white/10 bg-white/5 text-white"
                    />
                  </div>
                )}

                {!hideSameAs && otherItems.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-gray-200">Same content as</Label>
                    <Select
                      value={item.sameAsDeliverableId || '__none__'}
                      onValueChange={(val: string) =>
                        updateItem(index, {
                          sameAsDeliverableId: val === '__none__' ? null : val,
                        })
                      }
                    >
                      <SelectTrigger className="border-white/10 bg-white/5 text-white">
                        <SelectValue placeholder="None (unique content)" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1c2a35] border-white/15 text-white">
                        <SelectItem
                          value="__none__"
                          className="focus:bg-white/10 focus:text-white text-gray-200"
                        >
                          None (unique content)
                        </SelectItem>
                        {otherItems.map((other) => {
                          const otherNumber = items.indexOf(other) + 1
                          return (
                            <SelectItem
                              key={other.id}
                              value={other.id}
                              className="focus:bg-white/10 focus:text-white text-gray-200"
                            >
                              #{otherNumber} — {other.platform} {other.format}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    {sameAsTarget && (
                      <p className="text-xs text-[#C0FD72]/70">
                        Will reuse content from #{items.indexOf(sameAsTarget) + 1}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      <Button
        type="button"
        variant="outline"
        onClick={() =>
          onChange([...items, { ...createDefaultDeliverable(), order: items.length }])
        }
        className="border-dashed border-[#C0FD72]/30 bg-[#C0FD72]/5 text-[#C0FD72] hover:bg-[#C0FD72]/10"
      >
        <Plus className="mr-2 h-4 w-4" />
        Add deliverable
      </Button>
    </div>
  )
}
